import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Route, Plus, Loader2, CalendarDays, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const RECORRIDO_ESTADO = {
  planificado: { l: 'Planificado', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  en_curso: { l: 'En curso', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  finalizado: { l: 'Finalizado', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};
export const fmtFecha = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');

const RecorridoCard = ({ r }) => {
  const est = RECORRIDO_ESTADO[r.estado] || RECORRIDO_ESTADO.planificado;
  const hechas = (r.resumen_visitas?.realizada || 0) + (r.resumen_visitas?.no_realizada || 0);
  return (
    <Link to={`/recorridos/${r.id}`} className="card-base p-4 block hover:shadow-md transition-shadow" data-testid={`recorrido-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-slate-900">{r.nombre}</p>
        <span className={`text-[11px] px-2 py-0.5 rounded border shrink-0 ${est.tone}`}>{est.l}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{fmtFecha(r.fecha_prevista)}</span>
        <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{r.mediador_nombre}</span>
        <span data-testid={`recorrido-visitas-count-${r.id}`}>{r.total_visitas} visitas · {hechas} gestionadas</span>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500" style={{ width: `${r.total_visitas ? (hechas / r.total_visitas) * 100 : 0}%` }} />
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Creado por {r.creador_nombre}</p>
    </Link>
  );
};

export default function RecorridosPage() {
  const { user } = useAuth();
  const canAll = ['superadmin', 'admin'].includes(user?.role);
  const [scope, setScope] = useState('mine');
  const [items, setItems] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/recorridos?scope=${scope}`, { withCredentials: true })
      .then(r => setItems(r.data))
      .catch(err => { toast.error(err.response?.data?.detail || 'No se pudieron cargar los recorridos'); setItems([]); });
    setItems(null);
    load();
    window.addEventListener('xenicos:recorridos-changed', load);
    window.addEventListener('xenicos:notifications-refreshed', load);
    window.addEventListener('focus', load);
    return () => {
      window.removeEventListener('xenicos:recorridos-changed', load);
      window.removeEventListener('xenicos:notifications-refreshed', load);
      window.removeEventListener('focus', load);
    };
  }, [scope]);

  return (
    <div className="space-y-5" data-testid="recorridos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="heading-1 flex items-center gap-2"><Route className="w-6 h-6" /> Mis recorridos</h1>
        <div className="flex items-center gap-2">
          {canAll && (
            <div className="flex rounded-lg border border-slate-200 p-0.5">
              {[['mine', 'Míos'], ['all', 'Todos']].map(([k, l]) => (
                <button key={k} onClick={() => setScope(k)} className={`text-xs px-3 py-1.5 rounded-md ${scope === k ? 'bg-slate-900 text-white' : 'text-slate-600'}`} data-testid={`recorridos-scope-${k}`}>{l}</button>
              ))}
            </div>
          )}
          <Link to="/mapa" className="btn-primary text-sm flex items-center gap-2" data-testid="nuevo-recorrido-btn"><Plus className="w-4 h-4" /> Nuevo recorrido</Link>
        </div>
      </div>
      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="card-base p-10 text-center text-slate-500" data-testid="recorridos-empty">
          Aún no hay recorridos. Selecciona inmuebles en el <Link to="/mapa" className="text-blue-700 underline">Mapa</Link> y pulsa «Crear recorrido».
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="recorridos-list">
          {items.map(r => <RecorridoCard key={r.id} r={r} />)}
        </div>
      )}
    </div>
  );
}
